// frontend/src/pages/GrupoProprietario.js
import React, { useEffect, useMemo, useState } from "react";
import "./Complemento.css";
import { jwtDecode } from "jwt-decode";
import { copiarTexto } from "../utils/clipboard";
import { FaEdit } from "react-icons/fa";

import ThemeProvider from "../theme/TemaMuiProvider";
import { Box, Button, TextField, TablePagination, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import muiTheme from "../theme/muiTheme";

function GrupoProprietario() {
  const [grupos, setGrupos] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [novoGrupo, setNovoGrupo] = useState("");
  const [editandoId, setEditandoId] = useState(null);
  const [nomeEditado, setNomeEditado] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [gruposPorPagina, setGruposPorPagina] = useState(5);
  const [loading, setLoading] = useState(true);

  const [gridMinimizado, setGridMinimizado] = useState(() => {
    const saved = localStorage.getItem("grupoProprietarioGridMinimizado");
    return saved ? saved === "1" : false;
  });

  useEffect(() => {
    localStorage.setItem("grupoProprietarioGridMinimizado", gridMinimizado ? "1" : "0");
  }, [gridMinimizado]);

  const token = localStorage.getItem("token");

  const tipoUsuario = useMemo(() => {
    if (!token) return "";

    try {
      const decoded = jwtDecode(token);
      return decoded.tipo
        ?.toString()
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    } catch {
      return "";
    }
  }, [token]);

  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeEditar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeExcluir = ["gestor", "administrador"].includes(tipoUsuario);

  useEffect(() => {
    carregarGrupos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carregarGrupos = () => {
    const token = localStorage.getItem("token");

    fetch("/api/grupo-proprietarios", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        const lista = Array.isArray(data) ? data : Array.isArray(data.grupos) ? data.grupos : [];

        setGrupos(lista);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Erro ao buscar grupos de proprietários:", err);
        setLoading(false);
      });
  };

  const handleCadastrar = (e) => {
    e.preventDefault();

    if (!podeCadastrar) {
      alert("Você não tem permissão para cadastrar grupos de proprietários.");
      return;
    }

    const nome = novoGrupo.trim().toUpperCase();

    if (!nome) {
      alert("Preencha o campo Grupo Proprietário.");
      return;
    }

    const jaExiste = grupos.some((g) => (g.nome || "").trim().toUpperCase() === nome);

    if (jaExiste) {
      alert("Esse Grupo Proprietário já está cadastrado.");
      return;
    }

    const token = localStorage.getItem("token");

    fetch("/api/grupo-proprietarios", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ nome }),
    })
      .then(async (res) => {
        if (res.status === 403) {
          alert("Você não tem permissão para cadastrar.");
          return;
        }

        const data = await res.json();

        if (data?.id && data?.nome) {
          setGrupos((prev) => [data, ...prev]);
          setPaginaAtual(1);
        } else {
          carregarGrupos();
        }

        setNovoGrupo("");
      })
      .catch((err) => console.error("Erro ao cadastrar grupo proprietário:", err));
  };

  const handleEdit = (grupo) => {
    setEditandoId(grupo.id);
    setNomeEditado(grupo.nome || "");
  };

  const handleSaveEdit = (id) => {
    if (!podeEditar) {
      alert("Você não tem permissão para editar grupos de proprietários.");
      return;
    }

    const nome = nomeEditado.trim().toUpperCase();

    if (!nome) {
      alert("Informe o nome do Grupo Proprietário.");
      return;
    }

    const token = localStorage.getItem("token");

    fetch(`/api/grupo-proprietarios/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ nome }),
    })
      .then(async (res) => {
        if (res.status === 403) {
          alert("Você não tem permissão para editar.");
          return;
        }

        setGrupos((prev) => prev.map((g) => (g.id === id ? { ...g, nome } : g)));

        setEditandoId(null);
        setNomeEditado("");
      })
      .catch((err) => console.error("Erro ao editar grupo proprietário:", err));
  };

  const handleDelete = (id) => {
    if (!podeExcluir) {
      alert("Você não tem permissão para excluir grupos de proprietários.");
      return;
    }

    if (!window.confirm("Deseja realmente excluir este Grupo Proprietário?")) return;

    const token = localStorage.getItem("token");

    fetch(`/api/grupo-proprietarios/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (res.status === 403) {
          alert("Você não tem permissão para excluir.");
          return;
        }

        setGrupos((prev) => prev.filter((g) => g.id !== id));
      })
      .catch((err) => console.error("Erro ao excluir grupo proprietário:", err));
  };

  const gruposFiltrados = grupos.filter((g) => (g.nome || "").toLowerCase().includes(filtro.toLowerCase()));

  const indexInicial = (paginaAtual - 1) * gruposPorPagina;
  const indexFinal = indexInicial + gruposPorPagina;
  const gruposPaginados = gruposFiltrados.slice(indexInicial, indexFinal);

  const handleCSV = () => {
    const BOM = "\uFEFF";
    const header = ["Grupo Proprietário"];

    const csvEscape = (value) => {
      const s = String(value ?? "");
      if (/[;"\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const linhas = gruposFiltrados.map((g) => [g.nome ?? ""].map(csvEscape));
    const csv = [header.map(csvEscape), ...linhas].map((l) => l.join(";")).join("\n");

    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "grupo-proprietarios.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  if (!podeVisualizar) {
    return (
      <ThemeProvider theme={muiTheme}>
        <div className="complemento-container">
          <h2>Grupo Proprietário</h2>
          <p className="erro">🔒 Você não tem permissão para visualizar esta tela.</p>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="complemento-container">
        <h2>Grupo Proprietário</h2>

        <div className="bloco-linha">
          <TextField
            label="Buscar Grupo Proprietário"
            value={filtro}
            onChange={(e) => {
              setFiltro(e.target.value);
              setPaginaAtual(1);
            }}
            fullWidth
            size="small"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon fontSize="small" sx={{ color: "var(--ig-t-9ca3af)" }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 52,
                display: "flex",
                alignItems: "center",
              },
              "& .MuiOutlinedInput-input": {
                padding: "14px 14px",
              },
              "& .MuiInputLabel-root": {
                transform: "translate(14px, 15px) scale(1)",
              },
              "& .MuiInputLabel-shrink": {
                transform: "translate(14px, -9px) scale(0.75)",
              },
            }}
          />

          <div className="filtro-botoes">
            <Button
              size="small"
              variant="outlined"
              type="button"
              onClick={() => {
                setFiltro("");
                setPaginaAtual(1);
              }}
              sx={{
                height: 52,
                color: "#2196f3",
                borderColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  borderColor: "#42a5f5",
                  backgroundColor: "rgba(33, 150, 243, 0.08)",
                },
              }}
            >
              Limpar Filtro
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={async () => {
                const texto = gruposFiltrados.map((g) => g.nome).join(", ");
                if (!texto.trim()) {
                  alert("Nada para copiar (lista vazia).");
                  return;
                }
                const ok = await copiarTexto(texto);
                alert(ok ? "Copiado!" : "Não foi possível copiar.");
              }}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                color: "#fff",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              <i className="fas fa-copy" style={{ marginRight: 6 }}></i>
              Copiar
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={handleCSV}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                color: "#fff",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              CSV
            </Button>
          </div>
        </div>

        <div className="grid-toggle-area right">
          {!gridMinimizado ? (
            <button
              type="button"
              className="btn-grid-toggle small danger"
              onClick={() => setGridMinimizado(true)}
              title="Minimizar o Grid"
              aria-label="Minimizar o Grid"
            >
              ↘ Minimizar
            </button>
          ) : (
            <button
              type="button"
              className="btn-grid-toggle small outline"
              onClick={() => setGridMinimizado(false)}
              title="Expandir o Grid"
              aria-label="Expandir o Grid"
            >
              ↗ Expandir
            </button>
          )}
        </div>

        {!gridMinimizado && (
          <div className="bloco-lista">
            {loading ? (
              <p>Carregando Grupos Proprietários...</p>
            ) : gruposFiltrados.length === 0 ? (
              <p className="erro">Nenhum Grupo Proprietário cadastrado.</p>
            ) : (
              <>
                <table>
                  <thead>
                    <tr>
                      <th>Grupo Proprietário</th>
                      <th style={{ width: "300px", textAlign: "center" }}>Ações</th>
                    </tr>
                  </thead>

                  <tbody>
                    {gruposPaginados.map((grupo) => (
                      <tr key={grupo.id}>
                        <td className="nome-complemento" style={{ paddingRight: "16px" }}>
                          {editandoId === grupo.id ? (
                            <input
                              value={nomeEditado}
                              onChange={(e) => setNomeEditado(e.target.value.toUpperCase())}
                              autoFocus
                              style={{
                                width: "100%",
                                boxSizing: "border-box",
                                background: "transparent",
                                border: "2px solid #1fa3ff",
                                borderRadius: "6px",
                                padding: "10px 12px",
                                color: "var(--ig-t-ffffff)",
                                fontWeight: 600,
                                outline: "none",
                              }}
                            />
                          ) : (
                            grupo.nome
                          )}
                        </td>

                        <td
                          className="botoes-acao"
                          style={{
                            width: "300px",
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "12px",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            {editandoId === grupo.id ? (
                              <Button
                                size="small"
                                variant="contained"
                                type="button"
                                startIcon={<i className="fas fa-floppy-disk" />}
                                onClick={() => handleSaveEdit(grupo.id)}
                                sx={{
                                  minWidth: 118,
                                  height: 36,
                                  fontWeight: 600,
                                  fontSize: "0.85rem",
                                  textTransform: "none",
                                  backgroundColor: "#2e7d32",
                                  color: "#fff",
                                  "&:hover": {
                                    backgroundColor: "#1b5e20",
                                  },
                                }}
                              >
                                Salvar
                              </Button>
                            ) : (
                              <>
                                {podeEditar && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    type="button"
                                    startIcon={<FaEdit />}
                                    onClick={() => handleEdit(grupo)}
                                    sx={{
                                      minWidth: 118,
                                      height: 36,
                                      fontWeight: 600,
                                      fontSize: "0.85rem",
                                      textTransform: "none",
                                      backgroundColor: "#1998a8",
                                      color: "#fff",
                                      "&:hover": {
                                        backgroundColor: "#177f8d",
                                      },
                                    }}
                                  >
                                    Editar
                                  </Button>
                                )}

                                {podeExcluir && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    type="button"
                                    startIcon={<i className="fas fa-trash" />}
                                    onClick={() => handleDelete(grupo.id)}
                                    sx={{
                                      minWidth: 118,
                                      height: 36,
                                      fontWeight: 600,
                                      fontSize: "0.85rem",
                                      textTransform: "none",
                                      backgroundColor: "#d32f2f",
                                      color: "#fff",
                                      "&:hover": {
                                        backgroundColor: "#b71c1c",
                                      },
                                    }}
                                  >
                                    Excluir
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <TablePagination
                  component="div"
                  count={gruposFiltrados.length}
                  page={paginaAtual - 1}
                  onPageChange={(_, novaPagina) => setPaginaAtual(novaPagina + 1)}
                  rowsPerPage={gruposPorPagina}
                  onRowsPerPageChange={(e) => {
                    setGruposPorPagina(parseInt(e.target.value, 10));
                    setPaginaAtual(1);
                  }}
                  rowsPerPageOptions={[5, 10, 25]}
                  labelRowsPerPage="Grupos por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                  slotProps={{
                    select: {
                      MenuProps: {
                        PaperProps: {
                          sx: {
                            bgcolor: "var(--ig-f-1e1e1e)",
                            color: "var(--ig-t-ffffff)",
                            "& .MuiMenuItem-root": {
                              color: "var(--ig-t-ffffff)",
                            },
                            "& .MuiMenuItem-root.Mui-selected": {
                              bgcolor: "var(--ig-f-2b2b2b)",
                            },
                            "& .MuiMenuItem-root:hover": {
                              bgcolor: "var(--ig-f-333333)",
                            },
                          },
                        },
                      },
                    },
                  }}
                  sx={{
                    fontSize: "0.8rem",
                    minHeight: 42,
                    color: "var(--ig-t-ffffff)",
                    borderTop: "1px solid var(--ig-b-444444)",
                    "& .MuiTablePagination-toolbar": {
                      minHeight: 42,
                    },
                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                      fontSize: "0.8rem",
                    },
                    "& .MuiTablePagination-selectIcon": {
                      color: "var(--ig-t-ffffff)",
                    },
                    "& .MuiTablePagination-actions button": {
                      color: "var(--ig-t-ffffff)",
                      width: "auto",
                    },
                    "& .MuiTablePagination-actions": {
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                />
              </>
            )}
          </div>
        )}

        {podeVisualizar && !podeCadastrar && (
          <div className="aviso-permissao-container">
            <p className="aviso-permissao">🔒 Você possui acesso apenas de visualização.</p>
          </div>
        )}

        {podeCadastrar && (
          <div className="bloco-formulario">
            <Box
              component="form"
              onSubmit={handleCadastrar}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <TextField
                id="grupoProprietario"
                label="Grupo Proprietário"
                value={novoGrupo}
                onChange={(e) => setNovoGrupo(e.target.value.toUpperCase())}
                placeholder="Digite o nome do Grupo Proprietário"
                required={false}
                fullWidth
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: 56,
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "16.5px 14px",
                  },
                  "& .MuiInputLabel-root": {
                    transform: "translate(14px, 16px) scale(1)",
                  },
                  "& .MuiInputLabel-shrink": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                startIcon={<i className="fas fa-save" />}
                sx={{
                  width: 240,
                  minWidth: 240,
                  height: 52,

                  alignSelf: "center",

                  textTransform: "none",
                  fontSize: "16px",
                  fontWeight: 700,

                  color: "#fff",

                  backgroundColor: "#2e7d32",

                  borderRadius: "6px",

                  boxShadow: "none",

                  "&:hover": {
                    backgroundColor: "#1b5e20",
                    boxShadow: "none",
                  },
                }}
              >
                Cadastrar
              </Button>
            </Box>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
}

export default GrupoProprietario;
